// src/components/calendar/EventModal.jsx
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getDrivers } from "../../services/driverService";

/**
 * EventModal Component
 *
 * Modal for creating and editing calendar events.
 * Handles form validation, event types, and CRUD operations.
 * Now includes additional event types: Reschedule, Pick Up, and Delivery
 */
const EventModal = ({
  isOpen,
  params,
  eventType,
  onClose,
  onInputChange,
  onEventTypeChange,
  onSave,
  onDelete,
}) => {
  // Drivers list for the "Assign to driver" dropdown. Fetched once per
  // modal open — drivers don't change often, no need for real-time.
  const [drivers, setDrivers] = useState([]);
  const [driversLoadError, setDriversLoadError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    getDrivers({ includeDisabled: false })
      .then((list) => {
        if (!cancelled) {
          setDrivers(list);
          setDriversLoadError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) setDriversLoadError(err.message || String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // Set default dates when modal opens for new events
  useEffect(() => {
    if (isOpen && !params.id && (!params.start || !params.end)) {
      const now = new Date();
      const startTime = new Date(now);
      const endTime = new Date(now);

      // Round to next hour for cleaner default
      startTime.setMinutes(0, 0, 0);
      startTime.setHours(startTime.getHours() + 1);

      // End time is 1 hour after start time
      endTime.setTime(startTime.getTime() + 60 * 60 * 1000);

      // Format for datetime-local input
      const formatForInput = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      };

      // Update start time if not set
      if (!params.start) {
        onInputChange({
          target: {
            id: "start",
            value: formatForInput(startTime),
          },
        });
      }

      // Update end time if not set
      if (!params.end) {
        onInputChange({
          target: {
            id: "end",
            value: formatForInput(endTime),
          },
        });
      }
    }
  }, [isOpen, params.id, params.start, params.end, onInputChange]);

  // Don't render if modal is closed
  if (!isOpen) return null;

  // ==========================================
  // EVENT HANDLERS
  // ==========================================

  /**
   * Handle form submission
   */
  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate required fields
    if (!eventType) {
      alert("Please select an event type");
      return;
    }

    if (!params.title || !params.start || !params.end) {
      alert("Please fill in all required fields");
      return;
    }

    // Validate that end date is after start date
    if (new Date(params.end) <= new Date(params.start)) {
      alert("End date must be after start date");
      return;
    }

    onSave();
  };

  /**
   * Handle backdrop click to close modal
   */
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  /**
   * Handle start date change and auto-adjust end date
   */
  const handleStartDateChange = (e) => {
    const startDate = new Date(e.target.value);
    const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // Add 1 hour

    // Format end date for datetime-local input
    const formatForInput = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    // Update start date
    onInputChange(e);

    // Auto-update end date to be 1 hour later
    onInputChange({
      target: {
        id: "end",
        value: formatForInput(endDate),
      },
    });
  };

  // ==========================================
  // RENDER
  // ==========================================

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleBackdropClick}
      />

      {/* Modal - Full screen on mobile, centered on desktop */}
      <div className="flex min-h-full items-end sm:items-center justify-center sm:p-4">
        <div className="relative bg-white w-full h-full sm:h-auto sm:rounded-lg sm:shadow-xl sm:max-w-3xl sm:max-h-[90vh] overflow-y-auto">
          {/* Modal Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 sm:rounded-t-lg z-10">
            <div className="flex items-center justify-between">
              <div className="flex-1 mr-2">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center">
                  {/* Calendar icon */}
                  <svg
                    className="w-5 h-5 sm:w-6 sm:h-6 mr-2 text-indigo-600 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <span className="truncate">
                    {params.id ? "Edit Event" : "Create New Event"}
                  </span>
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  {params.id
                    ? "Update event details"
                    : "Add a new event to your calendar"}
                </p>
              </div>

              {/* Close Button - Larger touch target for mobile */}
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200 p-2 rounded-full hover:bg-gray-100 flex-shrink-0"
                aria-label="Close modal"
              >
                <svg
                  className="w-6 h-6 sm:w-7 sm:h-7"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Modal Body */}
          <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-5 sm:space-y-6">
            {/* Event Title */}
            <div>
              <label
                htmlFor="title"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Event Title <span className="text-red-500">*</span>
              </label>
              <input
                id="title"
                type="text"
                value={params.title || ""}
                onChange={onInputChange}
                className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
                placeholder="Enter event title"
                required
              />
            </div>

            {/* Event Location — shows the job address saved with the event (if any).
                Editable so users can correct it or override for events held elsewhere. */}
            <div>
              <label
                htmlFor="location"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Location
                <span className="text-gray-500 font-normal text-xs sm:text-sm ml-2">
                  (job address, if saved with the event)
                </span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <svg
                    className="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
                <input
                  id="location"
                  type="text"
                  value={params.location || ""}
                  onChange={onInputChange}
                  className="w-full pl-10 pr-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
                  placeholder="Event location (e.g., job site address)"
                />
              </div>
            </div>

            {/* Assign to driver — surfaces driver in the driver portal once
                assigned. Service-layer hook then writes the driver UID into
                jobs/{jobId}.assignedDriverIds so Firestore Rules can grant
                read access to the parent job's rooms. */}
            <div>
              <label
                htmlFor="driverId"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Assign to fitter
                <span className="text-gray-500 font-normal text-xs sm:text-sm ml-2">
                  (optional)
                </span>
              </label>
              <select
                id="driverId"
                value={params.driverId || ""}
                onChange={onInputChange}
                className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
              >
                <option value="">— Unassigned —</option>
                {drivers.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                    {d.licensePlate ? ` · ${d.licensePlate}` : ""}
                  </option>
                ))}
              </select>
              {driversLoadError && (
                <p className="mt-1 text-xs text-red-600">
                  Couldn't load fitters: {driversLoadError}
                </p>
              )}
            </div>

            {/* Event Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Event Type <span className="text-red-500">*</span>
                {!params.id && (
                  <span className="text-gray-500 font-normal text-xs sm:text-sm">
                    {" "}
                    (select one)
                  </span>
                )}
              </label>

              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                {/* Measurement Option */}
                <label
                  className={`flex items-center justify-center p-4 sm:p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 min-h-[52px] ${
                    eventType === "measurement"
                      ? "bg-blue-700 border-blue-800 ring-4 ring-blue-300 shadow-lg text-white"
                      : "bg-blue-500 border-blue-500 hover:bg-blue-600 text-white"
                  }`}
                >
                  <input
                    type="radio"
                    name="eventType"
                    value="measurement"
                    checked={eventType === "measurement"}
                    onChange={(e) => onEventTypeChange(e.target.value)}
                    className="sr-only"
                  />
                  <div className="flex items-center">
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                    Measurement
                  </div>
                </label>

                {/* Installation Option */}
                <label
                  className={`flex items-center justify-center p-4 sm:p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 min-h-[52px] ${
                    eventType === "installation"
                      ? "bg-green-700 border-green-800 ring-4 ring-green-300 shadow-lg text-white"
                      : "bg-green-500 border-green-500 hover:bg-green-600 text-white"
                  }`}
                >
                  <input
                    type="radio"
                    name="eventType"
                    value="installation"
                    checked={eventType === "installation"}
                    onChange={(e) => onEventTypeChange(e.target.value)}
                    className="sr-only"
                  />
                  <div className="flex items-center">
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    Installation
                  </div>
                </label>

                {/* Pricing Option */}
                <label
                  className={`flex items-center justify-center p-4 sm:p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 min-h-[52px] ${
                    eventType === "price"
                      ? "bg-purple-700 border-purple-800 ring-4 ring-purple-300 shadow-lg text-white"
                      : "bg-purple-500 border-purple-500 hover:bg-purple-600 text-white"
                  }`}
                >
                  <input
                    type="radio"
                    name="eventType"
                    value="price"
                    checked={eventType === "price"}
                    onChange={(e) => onEventTypeChange(e.target.value)}
                    className="sr-only"
                  />
                  <div className="flex items-center">
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                      />
                    </svg>
                    Pricing
                  </div>
                </label>

                {/* Follow-up Option */}
                <label
                  className={`flex items-center justify-center p-4 sm:p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 min-h-[52px] ${
                    eventType === "follow-up"
                      ? "bg-teal-700 border-teal-800 ring-4 ring-teal-300 shadow-lg text-white"
                      : "bg-teal-500 border-teal-500 hover:bg-teal-600 text-white"
                  }`}
                >
                  <input
                    type="radio"
                    name="eventType"
                    value="follow-up"
                    checked={eventType === "follow-up"}
                    onChange={(e) => onEventTypeChange(e.target.value)}
                    className="sr-only"
                  />
                  <div className="flex items-center">
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                    Follow-up
                  </div>
                </label>

                {/* Quote Option */}
                <label
                  className={`flex items-center justify-center p-4 sm:p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 min-h-[52px] ${
                    eventType === "quote"
                      ? "bg-slate-700 border-slate-800 ring-4 ring-slate-300 shadow-lg text-white"
                      : "bg-slate-500 border-slate-500 hover:bg-slate-600 text-white"
                  }`}
                >
                  <input
                    type="radio"
                    name="eventType"
                    value="quote"
                    checked={eventType === "quote"}
                    onChange={(e) => onEventTypeChange(e.target.value)}
                    className="sr-only"
                  />
                  <div className="flex items-center">
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    Quote
                  </div>
                </label>

                {/* Reschedule Option */}
                <label
                  className={`flex items-center justify-center p-4 sm:p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 min-h-[52px] ${
                    eventType === "reschedule"
                      ? "bg-orange-700 border-orange-800 ring-4 ring-orange-300 shadow-lg text-white"
                      : "bg-orange-500 border-orange-500 hover:bg-orange-600 text-white"
                  }`}
                >
                  <input
                    type="radio"
                    name="eventType"
                    value="reschedule"
                    checked={eventType === "reschedule"}
                    onChange={(e) => onEventTypeChange(e.target.value)}
                    className="sr-only"
                  />
                  <div className="flex items-center">
                    <svg
                      className="w-5 h-5 mr-2 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    <span className="text-sm sm:text-base">Reschedule</span>
                  </div>
                </label>

                {/* Pick Up Option */}
                <label
                  className={`flex items-center justify-center p-4 sm:p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 min-h-[52px] ${
                    eventType === "pickup"
                      ? "bg-cyan-700 border-cyan-800 ring-4 ring-cyan-300 shadow-lg text-white"
                      : "bg-cyan-500 border-cyan-500 hover:bg-cyan-600 text-white"
                  }`}
                >
                  <input
                    type="radio"
                    name="eventType"
                    value="pickup"
                    checked={eventType === "pickup"}
                    onChange={(e) => onEventTypeChange(e.target.value)}
                    className="sr-only"
                  />
                  <div className="flex items-center">
                    <svg
                      className="w-5 h-5 mr-2 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                      />
                    </svg>
                    <span className="text-sm sm:text-base">Pick Up</span>
                  </div>
                </label>

                {/* Delivery Option */}
                <label
                  className={`flex items-center justify-center p-4 sm:p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 min-h-[52px] ${
                    eventType === "delivery"
                      ? "bg-red-700 border-red-800 ring-4 ring-red-300 shadow-lg text-white"
                      : "bg-red-500 border-red-500 hover:bg-red-600 text-white"
                  }`}
                >
                  <input
                    type="radio"
                    name="eventType"
                    value="delivery"
                    checked={eventType === "delivery"}
                    onChange={(e) => onEventTypeChange(e.target.value)}
                    className="sr-only"
                  />
                  <div className="flex items-center">
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0"
                      />
                    </svg>
                    Delivery
                  </div>
                </label>

                {/* Payment Option */}
                <label
                  className={`flex items-center justify-center p-4 sm:p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 min-h-[52px] ${
                    eventType === "payment"
                      ? "bg-emerald-700 border-emerald-800 ring-4 ring-emerald-300 shadow-lg text-white"
                      : "bg-emerald-500 border-emerald-500 hover:bg-emerald-600 text-white"
                  }`}
                >
                  <input
                    type="radio"
                    name="eventType"
                    value="payment"
                    checked={eventType === "payment"}
                    onChange={(e) => onEventTypeChange(e.target.value)}
                    className="sr-only"
                  />
                  <div className="flex items-center">
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 10h18M5 6h14a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2zm12 8h.01"
                      />
                    </svg>
                    Payment
                  </div>
                </label>

                {/* Repair Option */}
                <label
                  className={`flex items-center justify-center p-4 sm:p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 min-h-[52px] ${
                    eventType === "repair"
                      ? "bg-amber-700 border-amber-800 ring-4 ring-amber-300 shadow-lg text-white"
                      : "bg-amber-500 border-amber-500 hover:bg-amber-600 text-white"
                  }`}
                >
                  <input
                    type="radio"
                    name="eventType"
                    value="repair"
                    checked={eventType === "repair"}
                    onChange={(e) => onEventTypeChange(e.target.value)}
                    className="sr-only"
                  />
                  <div className="flex items-center">
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    Repair
                  </div>
                </label>
              </div>

              {/* Validation Message */}
              {!eventType && (
                <p className="text-red-500 text-sm mt-2 flex items-center">
                  <svg
                    className="w-4 h-4 mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Please select an event type
                </p>
              )}
            </div>

            {/* Date and Time Range */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Start Date & Time */}
              <div>
                <label
                  htmlFor="start"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Start Date & Time <span className="text-red-500">*</span>
                </label>
                <input
                  id="start"
                  type="datetime-local"
                  value={params.start || ""}
                  onChange={handleStartDateChange}
                  className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
                  required
                />
              </div>

              {/* End Date & Time */}
              <div>
                <label
                  htmlFor="end"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  End Date & Time <span className="text-red-500">*</span>
                </label>
                <input
                  id="end"
                  type="datetime-local"
                  value={params.end || ""}
                  min={params.start || ""}
                  onChange={onInputChange}
                  className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
                  required
                />
              </div>
            </div>

            {/* Event Description */}
            <div>
              <label
                htmlFor="description"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Description
              </label>
              <textarea
                id="description"
                rows={4}
                value={params.description || ""}
                onChange={onInputChange}
                className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-vertical transition-all duration-200"
                placeholder="Add any additional details about this event"
              />
            </div>

            {/* Modal Actions */}
            <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-6 border-t border-gray-200 sticky bottom-0 bg-white pb-4 sm:pb-0">
              {/* Cancel Button */}
              <button
                type="button"
                onClick={onClose}
                className="w-full sm:w-auto px-6 py-3 sm:py-2.5 text-base sm:text-sm bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 active:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 font-medium"
              >
                Cancel
              </button>

              {/* Delete Button (for existing events) */}
              {params.id && (
                <button
                  type="button"
                  onClick={() => onDelete(params.id)}
                  className="w-full sm:w-auto px-6 py-3 sm:py-2.5 text-base sm:text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 active:bg-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all duration-200 flex items-center justify-center font-medium"
                >
                  <svg
                    className="w-5 h-5 sm:w-4 sm:h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Mark Done
                </button>
              )}

              {/* Save Button */}
              <button
                type="submit"
                className="w-full sm:w-auto px-6 py-3 sm:py-2.5 text-base sm:text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 active:bg-indigo-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-200 flex items-center justify-center shadow-sm hover:shadow-md font-medium"
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                {params.id ? "Update Event" : "Create Event"}
              </button>

              {/* Go to Job Button (if linked to a job) */}
              {params.id && params.jobId && params.customerId && (
                <Link
                  to={`/customers/${params.customerId}/jobs/${params.jobId}/rooms`}
                  className="px-6 py-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 flex items-center"
                >
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                  Go to Job
                </Link>
              )}
            </div>
          </form>

          {/* Helper Text */}
          <div className="px-6 pb-6"></div>
        </div>
      </div>
    </div>
  );
};

export default EventModal;
